import {Injectable} from '@angular/core';
import {User} from './user';
import {BehaviorSubject, Observable} from 'rxjs/index';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {TranslateService} from '../shared/translate.service';
import {ModeService} from "../config/mode.service";
import {map} from "rxjs/internal/operators";
import {Router} from "@angular/router";
import {BaseUserService} from "./account/base-user.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseUserService {

  // nowe - START
  private currentUser: any;
  private rememberedUser: any;
  isLogged = new BehaviorSubject<boolean>(false);

  headers = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  // nowe - KONIEC

  authUnavailable = new BehaviorSubject<boolean>(false);

  user: User;

  authToken: string;
  authRefreshToken: string;

  constructor(private httpClient: HttpClient,
              private route: Router,
              private translateService: TranslateService,
              private modeService: ModeService
  ) {
    super();
    this.currentUser = localStorage.getItem('currentUser');
    this.rememberedUser = localStorage.getItem('rememberedUser');
    this.authToken = localStorage.getItem("authToken");
    this.authRefreshToken = localStorage.getItem("authRefreshToken");
    this.isLogged.next(!!this.currentUser);
  }

  async init() {
    try {
      const readOnly = await this.modeService.isReadOnly();
      if (!readOnly) {
        const onlineResult = await this.isAuthServiceOnline().toPromise();
        if (onlineResult) {
          this.authUnavailable.next(false);
          if (this.currentUserValue && this.currentUserValue.access_token) {
            this.checkPasswordStatus(this.currentUserValue.access_token).subscribe(isLogged => {
              if (!isLogged) {
                this.updateToken('', '');
              }
              this.isLogged.next(isLogged);
              return isLogged;
            })
          }
        }
      } else {
        this.authUnavailable.next(true);
        this.updateToken('', '');
      }
    } catch (error) {
      console.warn(error);
      this.authUnavailable.next(true);
      this.updateToken('', '');
    }
    return false;
  }

  public getToken(): Promise<string> {
    return Promise.resolve(this.authToken);
  }

  public validatePassword(password: string): boolean {
    if (this.currentUserValue && this.currentUserValue.password) {
      return password === this.currentUserValue.password;
    } else {
      return false;
    }
  }

  private updateToken(authToken: string, authRefreshToken) {
    this.authToken = authToken;
    this.authRefreshToken = authRefreshToken;
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authRefreshToken', authRefreshToken);
  }

  isAuthServiceOnline(): Observable<any> {
    return this.httpClient.get(environment.keycloakHealthcheck, {observe: 'response'});
  }

  public get rememberedUserValue(): User | null {
    this.rememberedUser = localStorage.getItem('rememberedUser');
    if (this.rememberedUser) {
      return JSON.parse(this.rememberedUser);
    } else return null;
  }

  public set rememberedUserValue(user: User | null) {
    localStorage.setItem('rememberedUser', JSON.stringify(user));
  }

  public get currentUserValue(): User | null {
    this.currentUser = localStorage.getItem('currentUser');
    if (this.currentUser) {
      return JSON.parse(this.currentUser);
    } else return null;
  }

  public set currentUserValue(user: User | null) {
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  resetPassword(email: string): Observable<any> {
    return this.httpClient.post(
      'portalApiUrl/omnis/auth/password-reset/' + email,
      {},
      {headers: this.headers, responseType: 'text'}
    );
  }

  changePassword(newPassword: string, token: string): Observable<any> {
    return this.httpClient.patch('portalApiUrl/omnis/auth/password-change',
      {newPassword: newPassword},
      {headers: this.createHeaders(token)}
    )
  }

  register(params: any): Observable<any> {
    return this.httpClient.post<any>('portalApiUrl/omnis/auth/register', params, {
      headers: this.headers,
    });
  }

  checkPasswordStatus(token: string): Observable<boolean> {
    return this.httpClient.get<boolean>('portalApiUrl/omnis/auth/is-password-init', {headers: this.createHeaders(token)});
  }

  rememberUser(email: string, password: string) {
    this.rememberedUserValue = {email: email, password: password};
  }

  forgetUser() {
    localStorage.removeItem('rememberedUser');
  }

  login(email: string, password: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      accept: 'application/json',
    });
    const params = new HttpParams()
      .set('email', email)
      .set('password', password);

    return this.httpClient.post<any>('portalApiUrl/omnis/auth/token', params, {headers: headers}).pipe(
      map((user) => {
        user.email = email;
        user.password = password;
        this.currentUserValue = user;
        this.updateToken(user.access_token, user.refresh_token);
        this.isLogged.next(true);
      })
    );
  }

  refreshToken(refreshToken: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      accept: 'application/json',
    });
    const params = new HttpParams()
      .set('refreshToken', refreshToken);
    return this.httpClient.post<any>('portalApiUrl/omnis/auth/refresh-token', params, {headers: headers})
      .pipe(
        map((user) => {
          this.currentUserValue.access_token = user.access_token;
          this.currentUserValue.refresh_token = user.refresh_token;
          this.updateToken(user.access_token, user.refresh_token);
          this.isLogged.next(true);
        })
      );
  }

  logout(redirectName?: string) {
    localStorage.removeItem('currentUser');
    this.isLogged.next(false);
    if (!redirectName) {
      this.route.navigate(['/']);
    } else {
      this.route.navigate([redirectName]);
    }
  }

  deleteAccount(token: string): Observable<any> {
    return this.httpClient.delete('portalApiUrl/omnis/auth/delete-user', {headers: this.createHeaders(token)});
  }
}
